import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Button } from 'src/shared/components/ui/buttons/button';
import { Loading } from 'src/shared/components/ui/loaders/loading';
import { useToast } from 'src/shared/components/ui/use-toast';
import Alert from 'src/shared/components/ui/alerts/alert';
import { Card, CardFooter } from 'src/shared/components/ui/card';
import { Skeleton } from 'src/shared/components/ui/loaders/skeleton';
import { Separator } from 'src/shared/components/ui/separator';
import { useAuth } from 'src/features/auth/contexts/AuthContext';
import { useLinkAccount } from 'src/shared/queries/useLinkAccount';
import { usePrimaryUser } from 'src/shared/queries/usePrimaryUser';
import { cn } from 'src/shared/lib/utils';

interface LinkAccountProps {
  email?: string;
  redirectUrl: string;
  className?: string;
}

export default function LinkAccount({ email, redirectUrl, className }: LinkAccountProps) {
  const { logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { linkAccount } = useLinkAccount();
  const {
    data: primaryUser,
    isLoading: loadingPrimaryUser,
    isError: isPrimaryUserError,
    refetch: refetchPrimaryUser,
  } = usePrimaryUser();
  const { errorToast } = useToast();

  const handleLinkAccounts = async () => {
    setLoading(true);
    if (!email) {
      errorToast('Failed linking accounts');
      return;
    }

    linkAccount.mutate(email, {
      onSuccess: () => {
        navigate({ to: redirectUrl || '/' });
      },
      onError: () => {
        errorToast('Failed linking accounts');
        setLoading(false);
      },
    });
  };

  return (
    <div>
      <img src="/static/images/tekst-logo.svg" width={80} height={20} className="mx-auto mb-4" alt="Tekst logo" />
      <Card className={cn('flex flex-col px-7 pt-6 pb-2 space-y-6', className)}>
        <div className=" w-full text-center">
          <p className="text-neutral-400">Account already exists</p>
        </div>
        <p className="text-center text-neutral-950">
          It looks like you already have another account with the same email address.
        </p>
        <Separator className="my-4 mx-auto" />
        {loadingPrimaryUser ? (
          <div className="text-center pb-4 space-y-4">
            <div className="space-y-2">
              <Skeleton className="h-4 w-3/4 mx-auto" />
              <Skeleton className="h-4 w-1/2 mx-auto" />
            </div>
            <div className="flex flex-col space-y-3 w-full mt-6">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-4 w-24 mx-auto" />
            </div>
          </div>
        ) : isPrimaryUserError ? (
          <div className="text-center pb-4 space-y-4">
            <Alert
              variant="error"
              title="Unable to load account details"
              description="We couldn't verify your account information. Please try again or return to login."
            />
            <CardFooter className="flex-center pb-0">
              <div className="flex flex-col space-y-3 w-full mt-2">
                <Button onClick={() => refetchPrimaryUser()} className="w-full" variant="default">
                  Try Again
                </Button>
                <Button onClick={logout} className="mt-3 flex-center text-neutral-400 text-xs hover:underline">
                  Back to login
                </Button>
              </div>
            </CardFooter>
          </div>
        ) : (
          <>
            <div className="text-center text-neutral-950">
              <p className="text-neutral-500 text-sm w-[70%] mx-auto">
                {primaryUser?.emailVerified
                  ? "You can link your accounts using the button below. You'll need to sign in again after linking."
                  : "To link these accounts, you'll need to verify the email address associated with your other account first."}
              </p>
            </div>
            <CardFooter className="flex-center pb-0">
              <div className="flex flex-col space-y-3 w-full mt-2">
                <div className={loading || !primaryUser?.emailVerified ? 'cursor-not-allowed' : ''}>
                  <Button
                    disabled={loading || !primaryUser?.emailVerified}
                    onClick={handleLinkAccounts}
                    className="w-full"
                    variant="default"
                  >
                    {loading ? <Loading color="white" size={16} /> : 'Link Accounts'}
                  </Button>
                </div>
                <Link
                  to={redirectUrl}
                  onClick={logout}
                  className="mt-3 flex-center text-neutral-400 text-xs hover:underline"
                >
                  Back to login
                </Link>
              </div>
            </CardFooter>
          </>
        )}
      </Card>
    </div>
  );
}
